/**
 * api/contact.ts —— 联系人能力
 *
 * 设计文档 5 章的 `contact.*` 落地。
 *
 * 这里有一个贯穿 M4 的核心设计决定：**用名字当句柄**。
 *
 * IL 没有变量。用户说「给王二发消息」，模型没法写：
 *   const w = contact.get('王二'); w.sendMessage('你好')
 * 因为它只能输出一条表达式链。所以 API 的形态是：
 *   session.send('王二', '你好')
 * 即把「找谁」作为参数在每一步重复声明。听起来低效，但它换来了两个好处：
 *   ① 模型不需要理解"值传递"这种概念，翻译难度陡降，出错率极低
 *   ② 每一句指令都自包含、可单独读懂 —— 用户看日志时一眼知道发生了什么
 *
 * 找人的规则：先精确匹配姓名，再匹配备注，最后按拼音模糊匹配。
 * 找不到就抛 NameError —— 而这个错误会回喂给 AI，
 * 它会看到「应用的联系人里没有『王二』」并据此解释或建议先建联系人。
 */

import { contactsRepo } from '@/data/repo.contacts';
import { sessionsRepo } from '@/data/repo.sessions';
import { messagesRepo } from '@/data/repo.messages';
import { pushOverlay } from '@/core/ui-store';
import { eventBus } from '@/core/event-bus';
import { RuntimeError, type ObjectDef } from '../registry';
import type { Contact } from '@/types/models';

/**
 * 按名字找联系人。
 *
 * 解析顺序（每一步都可能命中，命中即返回）：
 *   ① 姓名精确相等
 *   ② 备注精确相等
 *   ③ 姓名拼音键相等（用户可能打拼音）
 *   ④ 模糊包含（姓名或备注包含关键词）
 * 多条命中时取最早创建的 —— 确定性优先于"猜得准"。
 */
export async function resolveContact(nameOrRemark: string): Promise<Contact> {
  const target = nameOrRemark.trim();
  if (!target) throw new RuntimeError('要指定一个联系人名字', 'TypeError');

  const all = await contactsRepo.list();

  const exact = all.filter((c) => c.name === target);
  if (exact.length > 0) return exact[0];

  const byRemark = all.filter((c) => c.remark === target);
  if (byRemark.length > 0) return byRemark[0];

  const lower = target.toLowerCase();
  const byPinyin = all.filter((c) => c.pinyin === lower);
  if (byPinyin.length > 0) return byPinyin[0];

  const fuzzy = all.filter(
    (c) => c.name.includes(target) || c.remark.includes(target) || c.pinyin.includes(lower),
  );
  if (fuzzy.length > 0) return fuzzy[0];

  throw new RuntimeError(
    `联系人里没有「${target}」。可以先用 contact.create('${target}') 建一个。`,
    'NameError',
  );
}

export const contactApi = {
  name: 'contact',
  desc: '通讯录里的人和操作',
  userDesc: '管理通讯录 —— 新建、改名、搜索、删除联系人，看联系人名单',
  methods: [
    {
      name: 'list',
      kind: 'query',
      desc: '列出全部联系人。用户问「有哪些联系人」「通讯录里有谁」时用这个。',
      params: [],
      successText: '已读取联系人列表',
      run: async () => {
        const list = await contactsRepo.list();
        return { value: list };
      },
    },
    {
      name: 'count',
      kind: 'query',
      desc: '统计联系人数。用户问「通讯录里有几个人」时用这个。',
      params: [],
      successText: '已统计联系人数',
      run: async () => ({ value: await contactsRepo.count() }),
    },
    {
      name: 'create',
      desc: '新建一个联系人。用户说「加个联系人叫 xxx」「新建联系人 xxx」时用这个。若同名已存在，直接返回已有的那个（不会重复创建）。',
      params: [
        { name: 'name', type: 'string' as const, desc: '姓名', required: true },
        { name: 'remark', type: 'string' as const, desc: '备注名（可选）', required: false },
      ],
      successText: (args: unknown[]) => `已新建联系人「${String(args[0])}」`,
      run: async (args: unknown[]) => {
        const [name, remark] = args as [string, string | undefined];
        const existing = await contactsRepo.getByName(name);
        // 有就返回（且文案由求值器改成「已找到」，见下方 text 覆盖），没有才真建。
        // 之所以不直接返回 existing 了事：模型需要知道"有没有真新建"，
        // 否则它下一轮可能又说一遍「已为你新建」，实际上什么都没发生。
        if (existing) {
          return { value: existing, text: `联系人「${existing.name}」已经存在` };
        }
        const c = await contactsRepo.create({ name, remark });
        return { value: c };
      },
    },
    {
      name: 'search',
      kind: 'query',
      desc: '按姓名或备注搜索联系人，支持拼音首字母。用户说「找一下叫 xx 的人」时用这个。',
      params: [{ name: 'keyword', type: 'string' as const, desc: '搜索词', required: true }],
      successText: (args: unknown[]) => `已按「${String(args[0])}」搜索联系人`,
      run: async (args: unknown[]) => {
        const hits = await contactsRepo.search(String(args[0]));
        return { value: hits };
      },
    },
    {
      name: 'rename',
      desc: '给联系人改名。用户说「把 xxx 改成 yyy」时用这个。',
      params: [
        { name: 'name', type: 'string' as const, desc: '原姓名', required: true },
        { name: 'newName', type: 'string' as const, desc: '新姓名', required: true },
      ],
      successText: (args: unknown[]) => `已把「${String(args[0])}」改名为「${String(args[1])}」`,
      run: async (args: unknown[]) => {
        const c = await resolveContact(String(args[0]));
        await contactsRepo.update(c.id, { name: String(args[1]) });
        return {};
      },
    },
    {
      name: 'setSignature',
      desc: '设置联系人的个性签名。',
      params: [
        { name: 'name', type: 'string' as const, desc: '姓名', required: true },
        { name: 'signature', type: 'string' as const, desc: '签名内容', required: true },
      ],
      successText: (args: unknown[]) => `已更新「${String(args[0])}」的签名`,
      run: async (args: unknown[]) => {
        const c = await resolveContact(String(args[0]));
        await contactsRepo.update(c.id, { signature: String(args[1]) });
        return {};
      },
    },
    {
      name: 'remove',
      desc: '删除联系人（连同其会话与消息）。用户说「删掉联系人 xxx」时用这个。',
      params: [{ name: 'name', type: 'string' as const, desc: '姓名', required: true }],
      successText: (args: unknown[]) => `已删除联系人「${String(args[0])}」`,
      // 删除是不可逆的，必须让用户点头
      requireConfirm: (args: unknown[]) =>
        `确定要删除联系人「${String(args[0])}」吗？他/她的所有聊天记录也会一并删除，且无法恢复。`,
      run: async (args: unknown[]) => {
        const c = await resolveContact(String(args[0]));
        await contactsRepo.remove(c.id);
        return { value: { id: c.id, name: c.name } };
      },
    },
    {
      name: 'sendMessage',
      desc: '给某个联系人发一条消息。用户说「给 xxx 发消息说 yyy」「告诉 xxx 我到了」时用这个。',
      params: [
        { name: 'name', type: 'string' as const, desc: '联系人姓名', required: true },
        { name: 'text', type: 'string' as const, desc: '消息内容', required: true },
      ],
      successText: (args: unknown[]) => `已给「${String(args[0])}」发送：${String(args[1])}`,
      run: async (args: unknown[]) => {
        const [name, text] = args as [string, string];
        const c = await resolveContact(name);
        const session = await sessionsRepo.ensureForContact(c.id);
        await messagesRepo.append({ sessionId: session.id, sender: 'me', text });
        return {};
      },
    },
    {
      name: 'open',
      desc: '打开与某联系人的聊天界面。用户说「打开和 xxx 的聊天」「看看 xxx 的会话」时用这个。',
      params: [{ name: 'name', type: 'string' as const, desc: '联系人姓名', required: true }],
      successText: (args: unknown[]) => `已打开与「${String(args[0])}」的会话`,
      run: async (args: unknown[]) => {
        const c = await resolveContact(String(args[0]));
        const session = await sessionsRepo.ensureForContact(c.id);
        pushOverlay({ kind: 'chat', refId: session.id });
        eventBus.emit('session:opened', { sessionId: session.id });
        return { value: session };
      },
    },
  ],
} satisfies ObjectDef;
